import { useState, useContext, useCallback } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useBlogs = () => {
  const { USERNAME, PASSWORD, api } = useContext(GlobalContext);

  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [singleBlog, setSingleBlog] = useState(null);
  const [authorBlogs, setAuthorBlogs] = useState([]);
  const [authorData, setAuthorData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ================= GET ALL BLOGS =================
  const fetchAllBlogs = useCallback(async () => {
    if (!USERNAME || !PASSWORD) return;

    setLoading(true);
    try {
      const response = await api.post(
        "https://blogapi.packky.com/api/Bumppy/GetAllProductBlog",
        { webid: "7" },
        {
          auth: { username: USERNAME, password: PASSWORD },
        }
      );

      if (response.data.status === "0") {
        const formatted = response.data.data.map((post) => ({
          id: post.id,
          image: post.image_url,
          title: post.title,
          description: post.excerpt,
          author: post.author_name,
          post_slug: post.slug,
        }));

        setBlogs(formatted);
      }
    } catch (err) {
      setError("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  }, [api, USERNAME, PASSWORD]);

// ================= GET BLOG BY SLUG =================
const fetchBlogBySlug = useCallback(async (slug) => {
  if (!slug || !USERNAME || !PASSWORD) return;

  setLoading(true);

  try {
    const response = await api.post(
      "https://blogapi.packky.com/api/Bumppy/GetProductBlogBySlug",
      { slug },
      {
        auth: {
          username: USERNAME,
          password: PASSWORD,
        },
      }
    );

    if (response.data.status === "0") {
      const post = response.data.data[0];

      //  Convert blocks to HTML
      const htmlContent = post.blocks
        ?.map((block) => {
          if (!block.block_content) return "";

          switch (block.block_type) {
            case "h1":
              return `<h1>${block.block_content}</h1>`;
            case "h2":
              return `<h2>${block.block_content}</h2>`;
            case "h3":
              return `<h3>${block.block_content}</h3>`;
            case "p":
              return `<p>${block.block_content}</p>`;
            default:
              return `<p>${block.block_content}</p>`;
          }
        })
        .join("");

      setSingleBlog({
        id: post.id,
        image: post.image_url,
        title: post.title,
        description: post.excerpt,
        author: post.author_name,
        author_slug: post.author_slug,
        post_slug: post.slug,
        post_content: htmlContent,   
        post_keywords: post.keyword || "",
        created_at: post.in_time,
      });
    } else {
      setSingleBlog(null);
    }
  } catch (err) {
    setError("Failed to fetch blog");
  } finally {
    setLoading(false);
  }
}, [api, USERNAME, PASSWORD]);

  // ================= GET FEATURED BLOGS =================
  const fetchFeaturedBlogs = useCallback(async () => {
    try {
      const response = await api.post(
        "/Blog/GetProductBlogs",
        { product_name: "anlook" }
      );

      if (response.data.status === "0") {
        const formatted = response.data.data.slice(0, 10).map((post) => ({
          id: post.post_id,
          image: post.post_featured_image,
          title: post.post_title,
          post_slug: post.post_slug,
        }));

        setFeaturedBlogs(formatted);
      }
    } catch (err) {
      console.error("Featured blog fetch error");
    }
  }, [api]);

  // ================= GET BLOGS BY AUTHOR =================
  const fetchBlogsByAuthor = useCallback(async (authorSlug) => {
    if (!authorSlug) return;

    setLoading(true);

    try {
      const response = await api.post(
        "/api/Blog/GetBlogsByAuthors",
        { author_slug: authorSlug }
      );

      if (response.data.status === "0") {
        const authorInfo = response.data.data[0];

        setAuthorData({
          name: authorInfo.author_name,
          image: authorInfo.author_image,
          slug: authorInfo.author_slug,
        });

        const filteredPosts = authorInfo.posts.filter(
          (post) => post.product_name === "Anlook"
        );

        setAuthorBlogs(filteredPosts);
      }
    } catch (err) {
      setError("Failed to fetch author blogs");
    } finally {
      setLoading(false);
    }
  }, [api]);

  return {
    blogs,
    featuredBlogs,
    singleBlog,
    authorBlogs,
    authorData,
    loading,
    error,
    fetchAllBlogs,
    fetchBlogBySlug,
    fetchFeaturedBlogs,
    fetchBlogsByAuthor,
  };
};

export default useBlogs;